/**
 * City Pin — dramatic single pin drop onto a stylized map.
 */
import type { SceneBuilder } from "../../reel/scenes.js";
import { brandBackdrop } from "../../primitives/brandBackdrop.js";
import {
  accentFor, clipOutroFade, combineSceneFragments, escapeHtml, makeTl,
  typeSize, weights, withAlpha, colors as themeColors, gsapSetLine,
} from "../../reel/sceneShared.js";

export const cityPin: SceneBuilder = ({ ctx, clipPrefix, inputs, clipDurationSec, clipStartSec }) => {
  const accent = accentFor(inputs);
  const headline = inputs.text ?? "";
  const sub = inputs.subtext ?? "";

  const lg = typeSize("lg", ctx.format);
  const sm = typeSize("sm", ctx.format);

  const wrapId = `${clipPrefix}-wrap`;
  const headId = `${clipPrefix}-head`;
  const subId = `${clipPrefix}-sub`;
  const pinId = `${clipPrefix}-pin`;
  const pulseId = `${clipPrefix}-pulse`;
  const ring1Id = `${clipPrefix}-r1`;
  const ring2Id = `${clipPrefix}-r2`;
  const mapId = `${clipPrefix}-map`;

  const t = makeTl(clipStartSec);
  const backdrop = brandBackdrop(ctx, `${clipPrefix}-bg`, { variant: "cinematic", accent });

  const html = `<div id="${wrapId}" class="clip" data-start="${clipStartSec.toFixed(4)}" data-duration="${clipDurationSec.toFixed(4)}" style="position:absolute;inset:0;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:36px;padding:80px;">
    <div style="display:flex;flex-direction:column;align-items:center;gap:12px;text-align:center;">
      ${headline ? `<div id="${headId}" style="font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${lg * 0.78}px;letter-spacing:-2px;color:${themeColors.fg};line-height:1.02;text-wrap:balance;max-width:1080px;">${escapeHtml(headline)}</div>` : ""}
      ${sub ? `<div id="${subId}" style="font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${sm * 0.85}px;letter-spacing:6px;text-transform:uppercase;color:${accent};">${escapeHtml(sub)}</div>` : ""}
    </div>
    <div id="${mapId}" style="position:relative;width:${ctx.format === "9:16" ? 640 : 720}px;height:${ctx.format === "9:16" ? 640 : 720}px;max-width:100%;border-radius:9999px;background:radial-gradient(circle, ${withAlpha(accent, 0.12)} 0%, ${withAlpha(themeColors.bg, 0.6)} 70%);border:1.5px solid ${withAlpha(themeColors.fg, 0.1)};display:flex;align-items:center;justify-content:center;overflow:hidden;">
      <svg viewBox="0 0 100 100" preserveAspectRatio="none" style="position:absolute;inset:0;width:100%;height:100%;opacity:0.5;">
        ${[20, 35, 50, 65, 80].map((y) => `<path d="M0 ${y} Q25 ${y - 6} 50 ${y} T100 ${y}" stroke="${withAlpha(accent, 0.25)}" stroke-width="0.3" fill="none"/>`).join("")}
        ${[20, 35, 50, 65, 80].map((x) => `<path d="M${x} 0 Q${x + 4} 25 ${x} 50 T${x} 100" stroke="${withAlpha(accent, 0.18)}" stroke-width="0.3" fill="none"/>`).join("")}
      </svg>
      <div id="${ring2Id}" style="position:absolute;width:60%;height:60%;border-radius:9999px;border:1.5px dashed ${withAlpha(accent, 0.4)};"></div>
      <div id="${ring1Id}" style="position:absolute;width:30%;height:30%;border-radius:9999px;border:1.5px solid ${withAlpha(accent, 0.6)};box-shadow:0 0 30px ${withAlpha(accent, 0.4)} inset;"></div>
      <div id="${pulseId}" style="position:absolute;width:120px;height:120px;border-radius:9999px;background:${withAlpha(accent, 0.35)};"></div>
      <svg id="${pinId}" width="96" height="120" viewBox="0 0 24 30" style="position:relative;z-index:2;filter:drop-shadow(0 12px 30px rgba(0,0,0,0.6));">
        <path d="M12 0 C5 0 0 5 0 12 c0 9 12 18 12 18 s12-9 12-18 c0-7-5-12-12-12z" fill="${accent}"/>
        <circle cx="12" cy="11" r="4" fill="${themeColors.bg}"/>
      </svg>
    </div>
  </div>`;

  const init = [
    headline ? gsapSetLine(`#${headId}`, { opacity: 0, y: 16 }) : "",
    sub ? gsapSetLine(`#${subId}`, { opacity: 0, y: 10 }) : "",
    gsapSetLine(`#${mapId}`, { opacity: 0, scale: 0.92 }),
    gsapSetLine(`#${pinId}`, { opacity: 0, y: -200, scale: 0.7 }),
    gsapSetLine(`#${pulseId}`, { opacity: 0, scale: 0.5 }),
    gsapSetLine(`#${ring1Id}`, { opacity: 0, scale: 0.5 }),
    gsapSetLine(`#${ring2Id}`, { opacity: 0, scale: 0.5 }),
  ].filter(Boolean).join("\n");

  const tweens: string[] = [
    t.to(`#${mapId}`, { opacity: 1, scale: 1, duration: 0.7, ease: "raw:\"power2.out\"" }, 0.0),
    t.to(`#${ring2Id}`, { opacity: 1, scale: 1, duration: 0.7, ease: "raw:\"power2.out\"" }, 0.15),
    t.to(`#${ring2Id}`, { rotation: 360, duration: clipDurationSec, ease: "raw:\"none\"" }, 0),
    t.to(`#${ring1Id}`, { opacity: 1, scale: 1, duration: 0.7, ease: "raw:\"power2.out\"" }, 0.3),
    t.to(`#${pinId}`, { opacity: 1, y: 0, scale: 1, duration: 0.6, ease: "raw:\"bounce.out\"" }, 0.45),
    t.to(`#${pulseId}`, { opacity: 1, scale: 1, duration: 0.4, ease: "raw:\"power2.out\"" }, 0.95),
    t.to(`#${pulseId}`, { scale: 2.5, opacity: 0, duration: 1.5, repeat: -1, ease: "raw:\"power1.out\"" }, 1.0),
  ];
  if (headline) tweens.push(t.to(`#${headId}`, { opacity: 1, y: 0, duration: 0.55, ease: "raw:\"power2.out\"" }, 0.1));
  if (sub) tweens.push(t.to(`#${subId}`, { opacity: 1, y: 0, duration: 0.5, ease: "raw:\"power2.out\"" }, 0.25));
  tweens.push(clipOutroFade(clipPrefix, clipDurationSec, clipStartSec));

  return combineSceneFragments([backdrop, { html, init, tweens }]);
};
